import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import LoadingScreen from 'components/container-components/LoadingScreen';

// The @mercoa/react bundle is only needed for US companies with AP or AR
// enabled. Loading it eagerly on every page parsed hundreds of KB of JS
// that the majority of tenants never use. dynamic() defers it until
// MercoaSession actually renders (i.e. token is set below).
// loading fallback is required because MercoaProvider wraps the whole app:
// without it, the chunk-load window between "token set" and "MercoaSession
// mounted" renders null and the entire app below blanks out.
const MercoaSession = dynamic(() => import('@mercoa/react').then((m) => ({ default: m.MercoaSession })), {
  ssr: false,
  loading: () => <LoadingScreen />,
});
import theraToast from 'components/generic-components/toast/TheraToast';
import { useTheraFernClient } from 'hooks/useFern';
import { formatErrorMessage, logError } from 'utils/error';
import { isStytchProdEnv, isSuperAdminMode } from 'utils/user';
import { useCompany } from './company';
import { useUser } from './user';

export const MercoaProvider = ({ children }) => {
  const { theraFernApi } = useTheraFernClient();
  const { user } = useUser();
  const { company } = useCompany();
  const [token, setToken] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const isUSCompany = company?.address?.country === 'US';
  const isAP = company?.settings?.enabledApps?.ap;
  const isAR = company?.settings?.enabledApps?.ar;

  useEffect(() => {
    if (!theraFernApi || !user?.userID || !company?.companyID) return;
    if (!isUSCompany || (!isAP && !isAR)) return;
    if (isLoading || token) return;

    setIsLoading(true);
    theraFernApi.thera.company.companyApi
      .getMercoaToken(company.companyID, {
        userId: user.userID,
        expiresIn: '3d',
      })
      .then((resp) => {
        setToken(resp.token);
      })
      .catch((err) => {
        // thera-override: thera-toast-log-error-redundancy
        logError(err, { source: 'MercoaProvider' });
        if (!isStytchProdEnv || isSuperAdminMode) {
          theraToast.warning(`Failed to get Mercoa token: ${formatErrorMessage(err.body, '')}`, {
            id: 'mercoa-token-error',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.companyID, isUSCompany, user?.userID, token, isAP, isAR]);

  if (isLoading) return <LoadingScreen />;

  return token ? <MercoaSession token={token}>{children}</MercoaSession> : children;
};
